import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/lib/supabase';

export type Plan = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price_monthly_cents: number;
  price_yearly_cents: number;
  stripe_monthly_price_id: string | null;
  stripe_yearly_price_id: string | null;
  features: string[];
  is_active: boolean;
  display_order: number;
};

export type UserSubscription = {
  id: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  plan: Plan | null;
};

// ── All active plans (public) ─────────────────────────────────────────
export function usePlans() {
  return useQuery<Plan[]>({
    queryKey: ['plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .eq('is_active', true)
        .order('display_order');
      if (error) throw error;
      return (data ?? []).map((p) => ({
        ...p,
        features: Array.isArray(p.features) ? p.features : JSON.parse(p.features as string),
      })) as Plan[];
    },
  });
}

// ── Current user subscription ─────────────────────────────────────────
export function useSubscription() {
  const { user } = useAuth();
  return useQuery<UserSubscription | null>({
    queryKey: ['subscription', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select(`
          id, status, stripe_customer_id, stripe_subscription_id,
          current_period_start, current_period_end, cancel_at_period_end,
          plan:plans(*)
        `)
        .eq('user_id', user!.id)
        .neq('status', 'incomplete')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const plan = Array.isArray(data.plan) ? data.plan[0] : data.plan;
      return {
        ...data,
        plan: plan
          ? {
              ...plan,
              features: Array.isArray(plan.features)
                ? plan.features
                : JSON.parse(plan.features as string),
            }
          : null,
      } as UserSubscription;
    },
  });
}

/**
 * Returns whether the current user is on the Annual plan.
 * Used to gate early access to courses.
 */
export function useIsAnnualSubscriber() {
  const { data: sub } = useSubscription();
  return sub?.status === 'active' && sub.plan?.slug === 'annual';
}

// ── Open Stripe Customer Portal ───────────────────────────────────────
export function useOpenPortal() {
  return useMutation({
    mutationFn: async (returnPath = '/my-account') => {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: { returnPath },
      });
      if (error) throw error;
      if (!data?.url) throw new Error('No portal URL returned');
      window.location.href = data.url;
    },
    onError: (e) => toast.error((e as Error).message || 'Could not open billing portal'),
  });
}

// ── Start Stripe Checkout ─────────────────────────────────────────────
export function useStartCheckout() {
  return useMutation({
    mutationFn: async ({
      planSlug,
      billing = 'monthly',
    }: {
      planSlug: string;
      billing?: 'monthly' | 'yearly';
    }) => {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { planSlug, billing, mode: 'subscription', successPath: '/my-account?upgraded=1' },
      });
      if (error) throw error;
      if (!data?.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    },
    onError: (e) => toast.error((e as Error).message || 'Could not start checkout'),
  });
}
